/*
  GANGSTER — <gx-3d-stage>: one real-time 3D scene of the real hoodie model (gx-hoodie-gl.js).
  Used by the home page hero and product section (sections/gx-product-3d.liquid, mode "interactive") and by
  the scroll story reveal (sections/gs-story.liquid, mode "scroll").

  Markup:
    <gx-3d-stage data-mode="interactive|scroll" data-look="intro|studio" data-fit data-fit-width data-yaw
                 data-auto (deg/s idle turn) data-handoff (hero: takes the model over from the opening)
                 data-track="reveal" data-rect-from=".selector" (scroll mode)>
      <script type="application/json" data-model>{…gx-3d-model…}</script>
      <canvas></canvas> [data-gx-loader] [data-gx-fallback] [data-gx-hint]
    </gx-3d-stage>

  Life cycle, for performance:
    - files load once per page (shared cache); a scene creates its WebGL context when it comes near the
      viewport and releases it when it has been far away for a few seconds;
    - frames are drawn only while the scene is visible and something moves; the tab in the background,
      an open 3D viewer modal or a far-away scene draw nothing;
    - a failed load is retried once; without WebGL the scene shows one still product photo (no photo turn).
  Interaction (interactive mode): drag / one finger turns it (vertical page scroll stays native on touch),
  arrow keys, slow idle turn that pauses after interaction; reduced motion: no idle turn, no inertia.
  data-models (home banner and product section): one real GLB per colour ([{ key, values, model }]); the stage
  shows the colour chosen in the buy panel (gx-buy.js "gx-buy:change", kept for the visit) and switches with
  a short dim-out / light-up; each colour's model is uploaded once and kept until the stage is released.
  data-duo (home banner, two colours): both garments stand in the same scene — the chosen colour large in front,
  the other smaller further back on the right. A click on the one behind ([data-gx-back]) chooses its colour:
  the two glide past each other and swap places, each keeping its own angle.
  data-inspect (home banner): a click / tap on the garment, the "360° İNCELE" button or Enter brings the same
  model forward in the full-screen 3D viewer (gx-3d-viewer.js) at its current angle, for a manual 360° turn;
  closing it hands the garment back to the banner at the angle the shopper left it.
*/
(function () {
  'use strict';
  if (customElements.get('gx-3d-stage')) return;

  var root = document.documentElement;
  var reduce = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };
  var glScript = null;
  var paused = false;

  function clamp(x, a, b) {
    return x < a ? a : x > b ? b : x;
  }
  function smooth(u) {
    u = clamp(u, 0, 1);
    return u * u * (3 - 2 * u);
  }
  function wrap(deg) {
    deg = ((deg % 360) + 360) % 360;
    return deg > 180 ? deg - 360 : deg;
  }
  function easeInOut(u) {
    u = clamp(u, 0, 1);
    return u < 0.5 ? 4 * u * u * u : 1 - Math.pow(-2 * u + 2, 3) / 2;
  }
  function mixRect(a, b, u) {
    return { x: a.x + (b.x - a.x) * u, y: a.y + (b.y - a.y) * u, w: a.w + (b.w - a.w) * u, h: a.h + (b.h - a.h) * u };
  }

  // the garment behind: its share of the stage, how lit it stands, how long the swap takes
  var BACK_SCALE = 0.56;
  var BACK_LIGHT = 0.68;
  var GLIDE_MS = 950;

  // the colour the buy panel keeps for this visit (gx-buy.js, sessionStorage)
  function savedColor(product) {
    try {
      return (JSON.parse(sessionStorage.getItem('gx-buy:' + product) || '{}') || {}).color || null;
    } catch (e) {
      return null;
    }
  }

  function loadRenderer(src) {
    if (window.GXHoodieGL && window.GXHoodieGL.create) return Promise.resolve(window.GXHoodieGL);
    if (window.__gxHoodieGL) {
      return window.__gxHoodieGL.then(null, function () {
        return inject(src);
      });
    }
    return inject(src);
  }

  function inject(src) {
    if (!glScript) {
      glScript = new Promise(function (resolve, reject) {
        var s = document.createElement('script');
        s.src = src;
        s.async = true;
        s.onload = function () {
          if (window.GXHoodieGL && window.GXHoodieGL.create) resolve(window.GXHoodieGL);
          else reject(new Error('renderer'));
        };
        s.onerror = function () {
          glScript = null;
          reject(new Error('renderer'));
        };
        document.head.appendChild(s);
      });
    }
    return glScript;
  }

  /*
    Scroll tracks: scene progress → camera. p = how far the pinned scene has scrolled (0…1).
    "reveal" (home page story): the garment rises, faces the camera, then the camera visits the details the
    copy lists one by one — zipper and face-covering hood, chest print, sleeve print, back print — while
    the same model turns front → side → back. Keyframes are eased; the camera follows them with damping.
  */
  var TRACKS = {
    reveal: [
      // p,    yaw,  pitch, zoom, focus x, focus y
      [0.0, -28, 4, 1, 0, 0],
      [0.2, -12, 2, 1, 0, 0],
      [0.27, 0, 0, 1, 0, 0],
      [0.34, -8, -4, 2.3, 0.0, 0.3],
      [0.39, -8, -4, 2.3, 0.0, 0.3],
      [0.43, -14, 0, 2.5, 0.055, 0.06],
      [0.48, -14, 0, 2.5, 0.055, 0.06],
      [0.53, 78, 0, 1.6, -0.05, -0.11],
      [0.57, 92, 0, 1.6, -0.05, -0.11],
      [0.62, 180, 0, 1.45, 0, 0.08],
      [0.7, 180, 0, 1.15, 0, 0.04],
      [0.8, 180, 0, 1, 0, 0],
      [1.0, 180, 0, 1, 0, 0],
    ],
  };

  function trackPose(name, p) {
    var keys = TRACKS[name];
    if (p <= keys[0][0]) return keys[0];
    for (var i = 1; i < keys.length; i++) {
      if (p <= keys[i][0]) {
        var a = keys[i - 1];
        var b = keys[i];
        var u = smooth((p - a[0]) / (b[0] - a[0]));
        return a.map(function (v, k) {
          return v + (b[k] - v) * u;
        });
      }
    }
    return keys[keys.length - 1];
  }

  class Stage extends HTMLElement {
    connectedCallback() {
      if (this.ready !== undefined) return;
      try {
        this.spec = JSON.parse(this.querySelector('script[data-model]').textContent);
        var list = this.querySelector('script[data-models]');
        this.colors = list ? JSON.parse(list.textContent) : null;
      } catch (e) {
        return;
      }
      this.gpu = {};
      this.colorJobs = {};
      if (this.colors && this.colors.length) {
        var first = this.colorFor(savedColor(this.dataset.product)) || this.colors[0];
        this.colorKey = first.key;
        this.spec = first.model;
        this.dataset.color = first.key;
      }
      this.mode = this.dataset.mode || 'interactive';
      this.canvas = this.querySelector('canvas');
      this.rest = { yaw: Number(this.dataset.yaw || -24), pitch: Number(this.dataset.pitch || 3) };
      this.state = { yaw: this.rest.yaw, pitch: this.rest.pitch, zoom: 1, fx: 0, fy: 0, light: 0 };
      this.target = { yaw: this.rest.yaw, pitch: this.rest.pitch, zoom: 1, fx: 0, fy: 0, light: 0 };
      // the banner's second garment (data-duo): turned the other way, lit once its model is on the GPU
      this.duo = this.dataset.duo !== undefined && this.mode === 'interactive' && !!this.colors && this.colors.length === 2;
      this.back = { yaw: this.rest.yaw + 52, pitch: this.rest.pitch, light: 0 };
      this.glide = null;
      this.backButton = this.querySelector('[data-gx-back]');
      this.backFloor = this.querySelector('[data-gx-back-floor]');
      if (this.duo && this.backButton) {
        this.classList.add('is-duo');
        this.backButton.addEventListener('click', this.pickBack.bind(this));
        ['pointerenter', 'focus'].forEach(function (type) {
          this.backButton.addEventListener(type, this.hoverBack.bind(this, true));
        }, this);
        ['pointerleave', 'blur'].forEach(function (type) {
          this.backButton.addEventListener(type, this.hoverBack.bind(this, false));
        }, this);
      } else {
        this.duo = false;
      }
      this.velocity = 0;
      this.pointers = new Map();
      this.idleUntil = 0;
      this.raf = 0;
      this.ready = false;
      this.near = false;
      this.visible = false;
      this.attempt = 0;
      this.tick = this.tick.bind(this);
      this.setState('idle');

      this.onDown = this.handleDown.bind(this);
      this.onMove = this.handleMove.bind(this);
      this.onUp = this.handleUp.bind(this);
      this.onKey = this.handleKey.bind(this);
      this.onScroll = this.request.bind(this);
      this.onVisibility = this.request.bind(this);
      this.onModal = function (e) {
        var d = e.detail || {};
        paused = !!d.open;
        if (!d.open && this.inspecting) {
          this.inspecting = false;
          if (typeof d.yaw === 'number') {
            this.state.yaw = this.target.yaw = d.yaw;
            this.state.pitch = this.target.pitch = d.pitch;
            this.velocity = 0;
            this.idleUntil = performance.now() + 3000;
          }
        }
        this.request();
      }.bind(this);
      this.onBuyChange = function (e) {
        var d = e.detail || {};
        if (d.state && String(d.product) === this.dataset.product) this.setColor(d.state.color);
      }.bind(this);
      this.onIntroPrepare = function () {
        if (this.dataset.handoff !== undefined) this.prepare();
      }.bind(this);
      this.onIntroEnd = function () {
        if (this.ready && !this.handedOver) this.reveal();
        this.start();
        this.preloadColors();
      }.bind(this);

      if (this.mode === 'interactive') {
        this.addEventListener('pointerdown', this.onDown);
        this.addEventListener('pointermove', this.onMove);
        this.addEventListener('pointerup', this.onUp);
        this.addEventListener('pointercancel', this.onUp);
        this.addEventListener('keydown', this.onKey);
        if (this.dataset.inspect !== undefined) {
          var button = this.querySelector('[data-gx-inspect]');
          if (button) button.addEventListener('click', this.inspect.bind(this));
        }
      } else {
        this.scene = this.closest('[data-gs-scene]') || this;
        this.rectFrom = this.dataset.rectFrom ? this.scene.querySelector(this.dataset.rectFrom) : null;
        window.addEventListener('scroll', this.onScroll, { passive: true });
      }
      window.addEventListener('resize', this.onScroll);
      document.addEventListener('visibilitychange', this.onVisibility);
      document.addEventListener('gx3d:modal', this.onModal);
      document.addEventListener('gx:intro-prepare', this.onIntroPrepare);
      document.addEventListener('gx:intro-end', this.onIntroEnd);
      if (this.colors) document.addEventListener('gx-buy:change', this.onBuyChange);

      var self = this;
      this.nearObserver = new IntersectionObserver(
        function (entries) {
          self.near = entries[entries.length - 1].isIntersecting;
          if (self.near) {
            clearTimeout(self.releaseTimer);
            self.start();
          } else {
            // far away for a while: give the GPU memory back
            self.releaseTimer = setTimeout(self.release.bind(self), 4000);
          }
        },
        { rootMargin: '120% 0px' }
      );
      this.visibleObserver = new IntersectionObserver(function (entries) {
        self.visible = entries[entries.length - 1].isIntersecting;
        self.request();
      });
      this.nearObserver.observe(this.mode === 'scroll' ? this.scene : this);
      this.visibleObserver.observe(this.mode === 'scroll' ? this.scene : this);
    }

    disconnectedCallback() {
      if (!this.nearObserver) return;
      this.nearObserver.disconnect();
      this.visibleObserver.disconnect();
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      document.removeEventListener('visibilitychange', this.onVisibility);
      document.removeEventListener('gx3d:modal', this.onModal);
      document.removeEventListener('gx:intro-prepare', this.onIntroPrepare);
      document.removeEventListener('gx:intro-end', this.onIntroEnd);
      document.removeEventListener('gx-buy:change', this.onBuyChange);
      clearTimeout(this.releaseTimer);
      this.release();
    }

    setState(name) {
      this.dataset.state = name;
    }

    // nothing loads under the opening (it has the network and the GPU); the hero is prepared by the opening
    // shortly before it hands the model over, every other scene starts when the opening has ended
    waitingForIntro() {
      return root.classList.contains('gx-intro-on') && !this.introPrepared;
    }

    start() {
      if (this.renderer || this.loading) return this.loading;
      if (this.waitingForIntro()) return null;
      // reduced motion keeps the story's static layout (no scroll animation at all)
      if (this.mode === 'scroll' && !root.classList.contains('gs-motion')) return null;
      var self = this;
      var loader = setTimeout(function () {
        if (!self.ready) self.setState('loading');
      }, 250);
      this.loading = loadRenderer(this.dataset.gl)
        .then(function (GL) {
          return GL.fetchModel(self.spec).then(function (source) {
            if (!self.isConnected) return null;
            var r = GL.create(self.canvas, {
              look: self.dataset.look || 'intro',
              fit: Number(self.dataset.fit || 0.84),
              fitWidth: Number(self.dataset.fitWidth || 0.9),
              offsetY: Number(self.dataset.offsetY || 0),
              maxPixelRatio: window.innerWidth < 750 ? 1.75 : 2,
              // the scroll scene scales its frame up to 1.7× with CSS: render sharper than the frame
              supersample: Number(self.dataset.supersample || 1),
              maxSize: 2048,
              onLost: self.fail.bind(self),
            });
            if (!r) throw new Error('webgl');
            self.renderer = r;
            var key = self.colorKey || 'default';
            return r.load(source).then(function (model) {
              self.gpu[key] = model;
              r.use(model);
              self.shownKey = key;
              return true;
            });
          });
        })
        .then(function (ok) {
          clearTimeout(loader);
          self.loading = null;
          if (!ok || !self.renderer) return false;
          self.ready = true;
          self.attempt = 0;
          self.setState('ready');
          self.dispatchEvent(new CustomEvent('gx3d:ready', { bubbles: true }));
          if (!root.classList.contains('gx-intro-on') || self.dataset.handoff === undefined) self.reveal();
          // the colour changed while the first model was loading
          if (self.colorKey && self.colorKey !== self.shownKey) self.swapTo(self.colorEntry(self.colorKey));
          self.preloadColors();
          self.request();
          return true;
        })
        .catch(function (error) {
          clearTimeout(loader);
          self.loading = null;
          if (self.renderer) self.renderer.destroy();
          self.renderer = null;
          self.canvas = window.GXHoodieGL ? window.GXHoodieGL.freshCanvas(self.canvas) : self.canvas;
          // no WebGL at all: one still photo; a failed download: try once more before giving up
          if (String(error && error.message) !== 'webgl' && self.attempt++ < 1) {
            return new Promise(function (resolve) {
              setTimeout(resolve, 1200);
            }).then(function () {
              return self.start();
            });
          }
          self.fail(error);
          return false;
        });
      return this.loading;
    }

    // fade the model up from black (not used when the opening hands it over already lit)
    reveal() {
      if (this.revealed) return;
      this.revealed = true;
      this.state.light = reduce.matches ? 1 : 0;
      this.target.light = 1;
      if (this.mode === 'interactive' && !reduce.matches) {
        this.state.yaw = this.rest.yaw - 40;
        this.target.yaw = this.rest.yaw;
      }
      this.request();
    }

    // never silent: the reason goes to the console and the stage says the 3D model couldn't be shown
    fail(error) {
      var reason = (error && error.message) || 'WebGL context lost';
      console.error('[GANGSTER 3D] the 3D model could not be shown: ' + reason, this.spec);
      this.dataset.error = reason;
      this.ready = false;
      this.setState('error');
      var img = this.querySelector('[data-gx-fallback]');
      if (img && img.dataset.src && !img.getAttribute('src')) img.src = img.dataset.src;
      if (this.renderer) this.renderer.destroy();
      this.renderer = null;
    }

    /* ---------- colours ---------- */

    colorFor(value) {
      if (!this.colors || value == null) return null;
      var v = String(value).trim().toLowerCase();
      for (var i = 0; i < this.colors.length; i++) {
        if (this.colors[i].key === v || this.colors[i].values.indexOf(v) > -1) return this.colors[i];
      }
      return null;
    }

    colorEntry(key) {
      return this.colors.filter(function (c) {
        return c.key === key;
      })[0];
    }

    setColor(value) {
      var entry = this.colorFor(value);
      if (!entry) {
        // never silent: a colour the shopper can pick but no model answers to
        if (value != null && this.colors) {
          console.warn(
            '[GANGSTER 3D] no 3D model for the colour "' + value + '"; models: ' + JSON.stringify(this.colors.map(function (c) {
              return c.key + ' = ' + c.values.join('/');
            }))
          );
        }
        return;
      }
      if (entry.key === this.colorKey) return;
      this.colorKey = entry.key;
      this.spec = entry.model;
      this.dataset.color = entry.key;
      // not drawn yet: start() / the first load picks the new colour up
      if (this.renderer && this.ready) this.swapTo(entry);
    }

    // one colour's real GLB on this stage's GPU (downloaded once per page, shared with the other scenes)
    loadColor(entry) {
      var self = this;
      var r = this.renderer;
      var key = entry.key;
      if (this.gpu[key]) return Promise.resolve(this.gpu[key]);
      if (this.colorJobs[key]) return this.colorJobs[key];
      var job = loadRenderer(this.dataset.gl)
        .then(function (GL) {
          return GL.fetchModel(entry.model);
        })
        .then(function (source) {
          if (r !== self.renderer) throw new Error('released');
          return r.load(source);
        })
        .then(function (model) {
          if (r !== self.renderer) throw new Error('released');
          self.gpu[key] = model;
          // the banner shows it behind straight away
          if (self.duo) self.request();
          return model;
        });
      this.colorJobs[key] = job;
      job
        .catch(function () {})
        .then(function () {
          if (self.colorJobs[key] === job) delete self.colorJobs[key];
        });
      return job;
    }

    // the other colours wait ready in the background: loaded one after another once this stage shows its
    // garment and the opening has ended, so choosing a colour swaps the model at once
    preloadColors() {
      if (!this.colors || this.colors.length < 2 || !this.ready || root.classList.contains('gx-intro-on')) return;
      var self = this;
      var r = this.renderer;
      var rest = this.colors.filter(function (c) {
        return !self.gpu[c.key];
      });
      var later = window.requestIdleCallback
        ? function (fn) {
            window.requestIdleCallback(fn, { timeout: 1500 });
          }
        : function (fn) {
            setTimeout(fn, 200);
          };
      later(function next() {
        var entry = rest.shift();
        if (!entry || r !== self.renderer) return;
        self.loadColor(entry).then(
          function () {
            later(next);
          },
          function (error) {
            if (r === self.renderer) console.warn('[GANGSTER 3D] the ' + entry.key + ' model could not be loaded in the background: ' + (error && error.message));
            later(next);
          }
        );
      });
    }

    // the chosen colour's real GLB takes the garment's place: same stage, size, position and angle;
    // a short dim hides the swap. Chosen before it finished loading: the garment stays lit with the loader on.
    swapTo(entry) {
      var self = this;
      var r = this.renderer;
      var token = (this.swapToken = (this.swapToken || 0) + 1);
      this.classList.remove('is-swapping');
      // banner with both garments on stage: the one behind comes forward
      if (this.duo && this.gpu[entry.key] && this.shownKey && entry.key !== this.shownKey && !this.pending) {
        return this.glideTo(entry.key);
      }
      var show = function (model) {
        self.pending = entry.key === self.shownKey ? null : { key: entry.key, model: model };
        if (self.pending) self.fastFade = true;
        self.target.light = self.pending ? 0 : 1;
        self.request();
      };
      if (this.gpu[entry.key]) return show(this.gpu[entry.key]);
      this.pending = null;
      this.target.light = 1;
      this.classList.add('is-swapping');
      this.loadColor(entry).then(
        function (model) {
          if (token !== self.swapToken || r !== self.renderer) return;
          self.classList.remove('is-swapping');
          show(model);
        },
        function (error) {
          if (token !== self.swapToken) return;
          self.classList.remove('is-swapping');
          console.warn('[GANGSTER 3D] the ' + entry.key + ' model could not be loaded: ' + (error && error.message));
        }
      );
    }

    /* ---------- two garments on the banner (data-duo) ---------- */

    // the colour standing behind: the other model, once it is on this stage's GPU
    backEntry() {
      if (!this.duo || !this.shownKey) return null;
      var front = this.shownKey;
      var other = this.colors.filter(function (c) {
        return c.key !== front;
      })[0];
      return other && this.gpu[other.key] ? other : null;
    }

    // where each garment stands, in canvas pixels: the chosen one large and a little left, the other one
    // smaller and further back on the right (its feet higher up), partly behind it
    // (narrow, portrait stages: a little further apart so the one behind stays readable)
    duoFrames() {
      var w = this.canvas.clientWidth || 1;
      var h = this.canvas.clientHeight || 1;
      var narrow = w < h;
      var shift = narrow ? 0.13 : 0.09;
      var k = narrow ? 0.52 : BACK_SCALE;
      var cx = narrow ? 0.83 : 0.8;
      return {
        front: { x: -w * shift, y: 0, w: w, h: h },
        back: { x: w * cx - (w * k) / 2, y: h * 0.5 - (h * k) / 2, w: w * k, h: h * k },
        frontCenter: 0.5 - shift,
      };
    }

    // the garment's box inside its frame (it fills `fit` of the frame's height)
    garmentBox(frame) {
      var gh = frame.h * Number(this.dataset.fit || 0.84);
      var gw = gh * 0.66;
      return { x: frame.x + (frame.w - gw) / 2, y: frame.y + (frame.h - gh) / 2, w: gw, h: gh };
    }

    // the opening lands its hoodie exactly where the front garment stands (viewport pixels)
    heroFrame() {
      var r = this.canvas.getBoundingClientRect();
      if (!this.duo) return { x: r.left, y: r.top, w: r.width, h: r.height };
      var f = this.duoFrames().front;
      return { x: r.left + f.x, y: r.top + f.y, w: f.w, h: f.h };
    }

    // the button over the garment behind (only its visible part, the front garment keeps its own clicks),
    // its colour name under the feet, and its floor shadow
    placeBack(other) {
      var button = this.backButton;
      var show = !!other && !this.glide && this.back.light > 0.3;
      if (button.hidden === show) button.hidden = !show;
      if (this.backFloor) this.backFloor.classList.toggle('is-on', !!other && !this.glide);
      var frames = this.duoFrames();
      // the front garment's floor shadow and 360° button stand under its centre (gx-product-3d.css)
      var frontX = (frames.frontCenter * 100).toFixed(1) + '%';
      if (frontX !== this.frontX) {
        this.frontX = frontX;
        this.style.setProperty('--gx-front-x', frontX);
      }
      if (!other) return;
      var back = this.garmentBox(frames.back);
      var front = this.garmentBox(frames.front);
      var left = Math.max(back.x, front.x + front.w * 0.92);
      var box = [Math.round(left), Math.round(back.y), Math.round(back.x + back.w - left), Math.round(back.h)];
      var key = box.join() + other.key;
      if (key === this.backKey) return;
      this.backKey = key;
      button.style.left = box[0] + 'px';
      button.style.top = box[1] + 'px';
      button.style.width = box[2] + 'px';
      button.style.height = box[3] + 'px';
      var label = other.label || other.key;
      var caption = button.querySelector('[data-gx-back-label]');
      caption.textContent = label;
      caption.style.left = Math.round(back.x + back.w / 2 - box[0]) + 'px';
      button.setAttribute('aria-label', (button.dataset.label || '%c').replace('%c', label));
      if (this.backFloor) {
        this.backFloor.style.left = Math.round(back.x - back.w * 0.2) + 'px';
        this.backFloor.style.width = Math.round(back.w * 1.4) + 'px';
        this.backFloor.style.top = Math.round(back.y + back.h * 0.93) + 'px';
      }
    }

    hoverBack(on) {
      this.backHover = on;
      this.request();
    }

    // a click on the garment behind: its colour is chosen, exactly as in the buy panel, so the panel,
    // the Shopify variant and the 3D viewer follow (gx-buy.js answers "gx3d:color" with "gx-buy:change")
    pickBack() {
      var other = this.backEntry();
      if (!other || this.glide || (this.dragEnd && performance.now() - this.dragEnd < 400)) return;
      this.setColor(other.key);
      document.dispatchEvent(new CustomEvent('gx3d:color', { detail: { product: this.dataset.product, values: other.values } }));
    }

    // the two garments swap places; each keeps the angle it had, so nothing jumps
    glideTo(key) {
      var s = this.state;
      var t = this.target;
      var yaw = this.back.yaw;
      var pitch = this.back.pitch;
      this.back.yaw = s.yaw;
      this.back.pitch = s.pitch;
      s.yaw = t.yaw = yaw;
      s.pitch = t.pitch = pitch;
      this.velocity = 0;
      this.glide = { from: this.shownKey, t0: performance.now(), u: 0 };
      this.shownKey = key;
      this.renderer.use(this.gpu[key]);
      this.backHover = false;
      this.request();
    }

    duoStep(now, dt) {
      var b = this.back;
      var moving = false;
      var other = this.backEntry();
      var aim = other ? (this.backHover ? 1 : BACK_LIGHT) : 0;
      var dl = aim - b.light;
      if (Math.abs(dl) > 0.002) {
        b.light += dl * (reduce.matches ? 1 : 1 - Math.exp(-dt * 5));
        moving = true;
      } else {
        b.light = aim;
      }
      var auto = reduce.matches ? 0 : Number(this.dataset.auto || 0);
      if (other && auto && !this.userControl) {
        b.yaw += auto * dt;
        moving = true;
      }
      if (this.glide) {
        this.glide.u = reduce.matches ? 1 : clamp((now - this.glide.t0) / GLIDE_MS, 0, 1);
        if (this.glide.u >= 1) this.glide = null;
        moving = true;
      }
      this.placeBack(other);
      return moving;
    }

    // one frame of both garments, back to front
    drawDuo() {
      var s = this.state;
      var b = this.back;
      var frames = this.duoFrames();
      var front = {
        model: this.gpu[this.shownKey],
        yaw: s.yaw,
        pitch: s.pitch,
        zoom: s.zoom,
        focus: [s.fx, s.fy],
        light: s.light,
        rect: frames.front,
      };
      if (!front.model) return this.renderer.draw(this.pose());
      var g = this.glide;
      var behindKey = g ? g.from : this.backEntry() && this.backEntry().key;
      if (!behindKey || !this.gpu[behindKey] || (!g && b.light < 0.002)) return this.renderer.drawAll([front]);
      var behind = { model: this.gpu[behindKey], yaw: b.yaw, pitch: b.pitch, zoom: 1, focus: [0, 0], light: b.light * s.light, rect: frames.back };
      if (!g) return this.renderer.drawAll([behind, front]);
      var e = easeInOut(g.u);
      front.rect = mixRect(frames.back, frames.front, e);
      front.light = s.light * (BACK_LIGHT + (1 - BACK_LIGHT) * e);
      behind.rect = mixRect(frames.front, frames.back, e);
      behind.light = s.light * (1 - (1 - BACK_LIGHT) * e);
      this.renderer.drawAll(e < 0.5 ? [front, behind] : [behind, front]);
    }

    paint(rect) {
      if (this.duo) this.drawDuo();
      else this.renderer.draw(this.pose(rect));
    }

    release() {
      cancelAnimationFrame(this.raf);
      this.raf = 0;
      this.gpu = {};
      this.colorJobs = {};
      this.pending = null;
      this.shownKey = null;
      this.glide = null;
      this.back.light = 0;
      this.backKey = null;
      if (this.backButton) this.backButton.hidden = true;
      if (this.backFloor) this.backFloor.classList.remove('is-on');
      this.classList.remove('is-swapping');
      if (!this.renderer) return;
      this.renderer.destroy();
      this.renderer = null;
      this.ready = false;
      this.revealed = false;
      this.canvas = window.GXHoodieGL.freshCanvas(this.canvas);
      this.setState('idle');
    }

    /* ---------- hand-over from the opening ---------- */

    prepare() {
      this.introPrepared = true;
      var self = this;
      // the banner's second garment: its file downloads while the opening turns (shared cache), so it stands
      // behind as soon as the opening ends (the GPU upload waits for the end, see preloadColors)
      if (this.duo) {
        var front = this.colorKey;
        loadRenderer(this.dataset.gl)
          .then(function (GL) {
            self.colors.forEach(function (c) {
              if (c.key !== front) GL.fetchModel(c.model).catch(function () {});
            });
          })
          .catch(function () {});
      }
      return Promise.resolve(this.start()).then(function () {
        return self.ready;
      });
    }

    framing() {
      return {
        look: this.dataset.look || 'intro',
        fit: Number(this.dataset.fit || 0.84),
        fitWidth: Number(this.dataset.fitWidth || 0.9),
        offsetY: Number(this.dataset.offsetY || 0),
        rect: this.canvas.getBoundingClientRect(),
        yaw: this.rest.yaw,
        pitch: this.rest.pitch,
      };
    }

    takeOver(pose) {
      this.handedOver = true;
      this.revealed = true;
      ['yaw', 'pitch'].forEach(function (k) {
        this.state[k] = this.target[k] = pose[k];
      }, this);
      this.state.zoom = this.target.zoom = 1;
      this.state.light = this.target.light = 1;
      this.idleUntil = performance.now() + 900;
      this.classList.add('is-handed-over');
      if (this.renderer) this.paint();
      this.request();
    }

    /* ---------- frames ---------- */

    request() {
      // near but not started (e.g. the story's motion switched on after this element): start now
      if (!this.renderer && !this.loading && this.near && this.dataset.state !== 'error') this.start();
      if (!this.raf && this.renderer && this.ready) {
        this.last = performance.now();
        this.raf = requestAnimationFrame(this.tick);
      }
    }

    active() {
      return this.visible && !paused && !document.hidden;
    }

    pose(rect) {
      var s = this.state;
      return { yaw: s.yaw, pitch: s.pitch, zoom: s.zoom, focus: [s.fx, s.fy], light: s.light, rect: rect };
    }

    tick(now) {
      this.raf = 0;
      if (!this.renderer || !this.ready || !this.active()) return;
      var dt = Math.min(0.05, (now - this.last) / 1000);
      this.last = now;
      var s = this.state;
      var t = this.target;
      var rect = null;
      var moving = false;

      if (this.mode === 'scroll') {
        var box = this.scene.getBoundingClientRect();
        var vh = window.innerHeight;
        var p = clamp(-box.top / Math.max(box.height - vh, 1), 0, 1);
        var k = trackPose(this.dataset.track || 'reveal', p);
        t.yaw = k[1];
        t.pitch = k[2];
        t.zoom = k[3];
        t.fx = k[4];
        t.fy = k[5];
        if (this.rectFrom) {
          var c = this.canvas.getBoundingClientRect();
          var r = this.rectFrom.getBoundingClientRect();
          rect = { x: r.left - c.left, y: r.top - c.top, w: r.width, h: r.height };
          var key = [Math.round(rect.x), Math.round(rect.y), Math.round(rect.w)].join();
          if (key !== this.lastRect) moving = true;
          this.lastRect = key;
        }
      } else {
        var dragging = this.pointers.size > 0;
        if (!dragging && Math.abs(this.velocity) > 0.5 && !reduce.matches) {
          t.yaw += this.velocity * dt;
          this.velocity *= Math.exp(-dt * 4);
          moving = true;
        } else if (!dragging) {
          this.velocity = 0;
          var auto = reduce.matches ? 0 : Number(this.dataset.auto || 0);
          // the idle turn keeps the loop alive (only while visible); once the shopper has turned the
          // garment, opened the 360° view or used the arrow keys, it stops for good
          if (auto && !this.userControl) {
            if (now > this.idleUntil) t.yaw += auto * dt;
            moving = true;
          }
        }
        if (dragging) moving = true;
      }

      // follow the target with damping: no jumps when the page scrolls fast, a steady pose when it stops
      var f = reduce.matches ? 1 : 1 - Math.exp(-dt * (this.mode === 'scroll' ? 7 : 10));
      ['yaw', 'pitch', 'zoom', 'fx', 'fy'].forEach(function (name) {
        var d = t[name] - s[name];
        if (Math.abs(d) > (name === 'zoom' ? 0.0005 : name === 'yaw' || name === 'pitch' ? 0.01 : 0.0002)) moving = true;
        s[name] += d * f;
      });
      // colour change: once dimmed out, show the other model in the same pose and light it straight up
      if (this.pending && s.light < 0.04) {
        this.renderer.use(this.pending.model);
        this.shownKey = this.pending.key;
        this.pending = null;
        t.light = 1;
        moving = true;
      }
      var dl = t.light - s.light;
      if (Math.abs(dl) > 0.002) {
        moving = true;
        s.light += dl * (reduce.matches ? 1 : 1 - Math.exp(-dt * (this.fastFade ? 16 : 2.6)));
      } else {
        s.light = t.light;
        if (!this.pending) this.fastFade = false;
      }

      if (this.duo && this.duoStep(now, dt)) moving = true;
      this.paint(rect);
      if (moving) this.request();
    }

    /* ---------- interaction ---------- */

    handleDown(event) {
      if (!this.ready || (event.button !== undefined && event.button > 0)) return;
      // the garment behind is a button for a tap, but a drag across it still turns the front garment
      if (event.target.closest('a, button') && !event.target.closest('[data-gx-back]')) return;
      this.pointers.set(event.pointerId, {
        x: event.clientX,
        y: event.clientY,
        t: event.timeStamp,
        axis: null,
        sx: event.clientX,
        sy: event.clientY,
        st: event.timeStamp,
      });
      this.velocity = 0;
      this.classList.add('is-dragging');
      this.querySelectorAll('[data-gx-hint]:not(button)').forEach(function (h) {
        h.classList.add('is-done');
      });
      this.request();
    }

    handleMove(event) {
      var p = this.pointers.get(event.pointerId);
      if (!p) return;
      var dx = event.clientX - p.x;
      var dy = event.clientY - p.y;
      // a touch that starts vertical is the page scrolling (touch-action: pan-y), not a turn
      if (!p.axis && Math.abs(dx) + Math.abs(dy) > 4) {
        p.axis = Math.abs(dx) >= Math.abs(dy) ? 'x' : 'y';
        if (p.axis === 'x' && this.setPointerCapture) this.setPointerCapture(event.pointerId);
      }
      if (p.axis !== 'x' && event.pointerType === 'touch') return;
      // the shopper turns the garment: the idle turn stops for good
      this.userControl = true;
      var dt = Math.max(1, event.timeStamp - p.t);
      p.x = event.clientX;
      p.y = event.clientY;
      p.t = event.timeStamp;
      var deg = dx * 0.42;
      this.target.yaw += deg;
      this.state.yaw += deg * 0.6;
      if (event.pointerType !== 'touch') this.target.pitch = clamp(this.target.pitch + dy * 0.18, -14, 20);
      this.velocity = (deg / dt) * 1000;
      this.idleUntil = performance.now() + 3500;
      this.request();
    }

    handleUp(event) {
      var p = this.pointers.get(event.pointerId);
      if (!p) return;
      this.pointers.delete(event.pointerId);
      if (event.timeStamp - p.t > 90) this.velocity = 0;
      this.velocity = clamp(this.velocity, -700, 700);
      if (!this.pointers.size) this.classList.remove('is-dragging');
      this.idleUntil = performance.now() + 3500;
      // a click / tap (no drag) on the banner garment: bring it forward for the 360° view
      var moved = Math.abs(event.clientX - p.sx) + Math.abs(event.clientY - p.sy);
      // a drag that ended over the garment behind is not a click on it
      this.dragEnd = moved < 8 ? 0 : performance.now();
      var onBack = event.target.closest && event.target.closest('[data-gx-back]');
      if (event.type === 'pointerup' && moved < 8 && event.timeStamp - p.st < 500 && !onBack) this.inspect();
      this.request();
    }

    inspect() {
      if (this.dataset.inspect === undefined || !this.ready) return;
      var section = this.closest('.gx-p3d');
      var viewer = (section && section.querySelector('gx-3d-viewer')) || document.querySelector('gx-3d-viewer:not([hidden])');
      if (!viewer || !viewer.open) return;
      this.velocity = 0;
      this.inspecting = true;
      this.userControl = true;
      viewer.openedFromPage = true;
      viewer.open({ yaw: wrap(this.state.yaw), pitch: this.state.pitch });
    }

    handleKey(event) {
      var map = { ArrowLeft: -25, ArrowRight: 25 };
      if ((event.key === 'Enter' || event.key === ' ') && event.target === this && this.dataset.inspect !== undefined) {
        event.preventDefault();
        this.inspect();
        return;
      }
      if (event.key in map) {
        this.target.yaw += map[event.key];
      } else if (event.key === 'Home') {
        this.target.yaw += wrap(this.rest.yaw - this.target.yaw);
        this.target.pitch = this.rest.pitch;
      } else {
        return;
      }
      event.preventDefault();
      this.velocity = 0;
      this.userControl = true;
      this.idleUntil = performance.now() + 3500;
      this.request();
    }
  }

  customElements.define('gx-3d-stage', Stage);
})();
